/***
 *
 *   TICKET PROPERTIES
 *   The drawer's right rail. Status, priority, category, due date and
 *   assignees stay on screen while you read the conversation, instead of
 *   living behind a Details tab.
 *
 *   Priority is the one field this app can write from here, so it is the only
 *   one rendered as a control; the rest are read-only values.
 *
 **********/

import React, { useContext, useState, useEffect, useRef } from 'react';
import Style from './general.tailwind';
import { useTranslate } from 'app/translations';
import { useAttach, Icon, PriorityBadge, StatusBadge, AssigneeAvatars, cn } from 'components/lib';
import { AuthContext } from 'app/auth';
import { useApiContext } from 'app/api';
import { useMutation, useQueryClient } from 'react-query';
import Dropdown from 'components/dropdown/dropdown';
import { isTicketOverdue } from 'utilities/ticketDue';

const priorityDotColor = {
    urgent: 'bg-error',
    high: 'bg-warning',
    medium: 'bg-primary-600',
    low: 'bg-success',
};

function Row({ icon, label, children, align = 'center' }) {
    return (
        <div className={cn(Style.row, align === 'start' && 'items-start')}>
            <span className={Style.rowLabel}>
                <Icon image={icon} size={12} color="currentColor" />
                <span className="truncate">{label}</span>
            </span>
            <div className={Style.rowValue}>{children}</div>
        </div>
    );
}

const TicketGeneralInfo = ({ ticket, status }) => {
    const { _t } = useTranslate();
    const {
        user: { permission },
    } = useContext(AuthContext);
    const { callApi } = useApiContext();
    const queryClient = useQueryClient();
    const [selectedPriority, setSelectedPriority] = useState(ticket.priority);
    const priorityRef = useRef(null);

    const priorityOptions = [
        { label: _t('priority.low'), value: 'low' },
        { label: _t('priority.medium'), value: 'medium' },
        { label: _t('priority.high'), value: 'high' },
        { label: _t('priority.urgent'), value: 'urgent' },
    ];

    const { mutate: updatePriority, isLoading: isUpdatingPriority } = useMutation(
        (newPriority) =>
            callApi({
                url: `/api/ticket/${ticket.id}`,
                method: 'PATCH',
                data: { priority: newPriority },
            }),
        {
            onSuccess: () => {
                queryClient.refetchQueries('tickets');
            },
            onError: () => {
                setSelectedPriority(ticket.priority);
            },
        },
    );

    const handlePriorityChange = (newPriority) => {
        setSelectedPriority(newPriority);
        updatePriority(newPriority);
    };

    const priorityDropdownList = priorityOptions.map((option) => ({
        label: (
            <div className="flex items-center gap-2">
                <span
                    className={`inline-block h-3 w-3 rounded-full ${priorityDotColor[option.value]}`}
                />
                <span>{option.label}</span>
            </div>
        ),
        action: () => {
            handlePriorityChange(option.value);
            priorityToggle();
        },
    }));

    const { Component: PriorityDropdown, toggle: priorityToggle } = useAttach({
        ref: priorityRef,
        position: 'bottom',
        offset: 5,
        children: <Dropdown list={priorityDropdownList} />,
    });

    useEffect(() => {
        setSelectedPriority(ticket.priority);
    }, [ticket.priority]);

    const overdue = isTicketOverdue({ due_date: ticket.due_date, status: status?.key });

    return (
        <section className={Style.card}>
            <header className={Style.cardHeader}>
                <h3 className={Style.cardTitle}>{_t('ticket.detail.properties')}</h3>
            </header>

            <div className={Style.rows}>
                <Row icon="activity" label={_t('status.one')}>
                    <StatusBadge
                        status={status?.key}
                        label={_t(`ticket.category.status.${status?.key}`)}
                    />
                </Row>

                <Row icon="flag" label={_t('priority.label')}>
                    {permission === 'contact' ? (
                        <PriorityBadge priority={ticket.priority} />
                    ) : (
                        <div ref={priorityRef} className="relative inline-block">
                            <button
                                type="button"
                                disabled={isUpdatingPriority}
                                onClick={priorityToggle}
                                aria-label={_t('priority.label')}
                                className={cn(
                                    Style.priorityTrigger,
                                    isUpdatingPriority && 'opacity-50',
                                )}>
                                <PriorityBadge priority={selectedPriority} />
                                <Icon
                                    image="chevron-down"
                                    size={12}
                                    color="currentColor"
                                    className="text-text-muted"
                                />
                            </button>
                            <PriorityDropdown />
                        </div>
                    )}
                </Row>

                <Row icon="tag" label={_t('categories.label.one')}>
                    <span className={Style.value}>
                        {ticket.ticket_category?.name || _t('ticket.detail.noCategory')}
                    </span>
                </Row>

                <Row icon="calendar" label={_t('due.on')}>
                    {ticket.due_date ? (
                        <span className={cn(Style.value, overdue && Style.valueError)}>
                            {new Date(ticket.due_date).toLocaleDateString()}
                            {overdue && (
                                <span className={Style.overdueTag}>
                                    {_t('ticket.detail.overdue')}
                                </span>
                            )}
                        </span>
                    ) : (
                        <span className={Style.muted}>&mdash;</span>
                    )}
                </Row>

                {permission !== 'contact' && (
                    <Row icon="users" label={_t('assignee.to')} align="start">
                        {ticket.ticket_assignee?.length ? (
                            <AssigneeAvatars assignees={ticket.ticket_assignee} max={4} />
                        ) : (
                            <span className={Style.muted}>{_t('ticket.detail.unassigned')}</span>
                        )}
                    </Row>
                )}

                <Row icon="hash" label={_t('number')}>
                    <span className={Style.mono}>{ticket.number}</span>
                </Row>
            </div>

            {!ticket.is_synced && (
                <div className={Style.syncWarn}>
                    <p>{_t('integration.notSyncWarning')}</p>
                </div>
            )}
        </section>
    );
};

export default TicketGeneralInfo;
